class PageVideoTestimonials {
  constructor(root) {
    this.root = root;
    this.openButtons = Array.from(root.querySelectorAll('[data-video-open]'));
    this.modals = Array.from(root.querySelectorAll('[data-video-modal]'));
    this.closeButtons = Array.from(root.querySelectorAll('[data-video-close]'));
    this.modalOrigins = new Map();
    this.activeModal = null;
    this.lastFocusedElement = null;
    this.focusableNodes = [];

    this.onOpenClick = this.onOpenClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  init() {
    if (!this.openButtons.length || !this.modals.length) {
      return;
    }

    this.mountModalsToBody();

    this.openButtons.forEach((button) => {
      button.addEventListener('click', this.onOpenClick);
      button.setAttribute('aria-expanded', 'false');
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.onCloseClick);
    });

    document.addEventListener('keydown', this.onKeydown);
  }

  destroy() {
    this.openButtons.forEach((button) => {
      button.removeEventListener('click', this.onOpenClick);
    });

    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.onCloseClick);
    });

    document.removeEventListener('keydown', this.onKeydown);
    this.closeModal({ restoreFocus: false });
    this.unmountModalsFromBody();
  }

  onOpenClick(event) {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    const targetId = trigger.dataset.videoTarget;
    if (!targetId) {
      return;
    }

    const modal = document.getElementById(targetId);
    if (!(modal instanceof HTMLElement)) {
      return;
    }

    this.openModal(modal, trigger);
  }

  onCloseClick(event) {
    event.preventDefault();
    this.closeModal();
  }

  onKeydown(event) {
    if (!this.activeModal) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  openModal(modal, trigger) {
    if (this.activeModal === modal && modal.hidden === false) {
      return;
    }

    if (this.activeModal && this.activeModal !== modal) {
      this.closeModal({ restoreFocus: false });
    }

    this.lastFocusedElement = document.activeElement;
    this.activeModal = modal;
    this.activeTrigger = trigger;

    modal.hidden = false;
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
    });

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    document.body.classList.add('video-testimonials-lock');

    this.setupVideoPlayback(modal);
    this.cacheFocusableNodes(modal);

    const dialog = modal.querySelector('[data-video-dialog]');
    if (dialog instanceof HTMLElement) {
      dialog.focus();
    }
  }

  closeModal({ restoreFocus = true } = {}) {
    if (!this.activeModal) {
      return;
    }

    const modal = this.activeModal;
    modal.classList.remove('is-open');
    modal.hidden = true;

    this.pauseAndResetVideo(modal);
    document.body.classList.remove('video-testimonials-lock');

    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
    }

    if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus();
    }

    this.activeModal = null;
    this.activeTrigger = null;
    this.focusableNodes = [];
  }

  cacheFocusableNodes(modal) {
    const dialog = modal.querySelector('[data-video-dialog]');
    if (!(dialog instanceof HTMLElement)) {
      this.focusableNodes = [];
      return;
    }

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    this.focusableNodes = Array.from(dialog.querySelectorAll(selector)).filter(
      (node) => node instanceof HTMLElement && node.offsetParent !== null
    );
  }

  trapFocus(event) {
    if (!this.focusableNodes.length) {
      event.preventDefault();
      const dialog = this.activeModal?.querySelector('[data-video-dialog]');
      if (dialog instanceof HTMLElement) {
        dialog.focus();
      }
      return;
    }

    const first = this.focusableNodes[0];
    const last = this.focusableNodes[this.focusableNodes.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setupVideoPlayback(modal) {
    const videoElement = modal.querySelector('[data-video-element]');
    if (videoElement instanceof HTMLVideoElement) {
      videoElement.currentTime = 0;
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
      return;
    }

    const iframeElement = modal.querySelector('[data-video-iframe]');
    if (!(iframeElement instanceof HTMLIFrameElement)) {
      return;
    }

    const sourceType = iframeElement.dataset.sourceType || '';
    const externalUrl = iframeElement.dataset.externalUrl || '';
    const embedUrl = this.buildEmbedUrl(externalUrl, sourceType);
    if (embedUrl) {
      iframeElement.src = embedUrl;
    }
  }

  pauseAndResetVideo(modal) {
    const videoElement = modal.querySelector('[data-video-element]');
    if (videoElement instanceof HTMLVideoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
      return;
    }

    const iframeElement = modal.querySelector('[data-video-iframe]');
    if (iframeElement instanceof HTMLIFrameElement) {
      iframeElement.src = '';
    }
  }

  buildEmbedUrl(rawUrl, sourceType) {
    if (!rawUrl) {
      return '';
    }

    try {
      const parsed = new URL(rawUrl, window.location.origin);

      if (sourceType === 'youtube') {
        let videoId = '';
        if (parsed.hostname.includes('youtu.be')) {
          videoId = parsed.pathname.replace('/', '');
        } else {
          videoId = parsed.searchParams.get('v') || '';
        }
        if (!videoId) {
          return '';
        }
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
      }

      if (sourceType === 'vimeo') {
        const parts = parsed.pathname.split('/').filter(Boolean);
        const videoId = parts[parts.length - 1] || '';
        if (!videoId) {
          return '';
        }
        return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      }
    } catch (error) {
      return '';
    }

    return '';
  }

  mountModalsToBody() {
    this.modals.forEach((modal) => {
      if (!(modal instanceof HTMLElement)) {
        return;
      }

      const originParent = modal.parentNode;
      if (!originParent) {
        return;
      }

      const placeholder = document.createComment('video-testimonial-modal-anchor');
      originParent.insertBefore(placeholder, modal);
      this.modalOrigins.set(modal, { originParent, placeholder });
      document.body.appendChild(modal);
    });
  }

  unmountModalsFromBody() {
    this.modals.forEach((modal) => {
      const origin = this.modalOrigins.get(modal);
      if (!origin) {
        return;
      }

      const { originParent, placeholder } = origin;
      if (originParent && placeholder && placeholder.parentNode === originParent) {
        originParent.insertBefore(modal, placeholder);
        placeholder.remove();
      }
    });

    this.modalOrigins.clear();
  }
}

const sectionInstances = new Map();

function initVideoTestimonials(scope) {
  const root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
  const nodes = root.querySelectorAll('[data-video-testimonials]');

  nodes.forEach((node) => {
    if (sectionInstances.has(node)) {
      return;
    }

    const instance = new PageVideoTestimonials(node);
    instance.init();
    sectionInstances.set(node, instance);
  });
}

function destroyVideoTestimonials(scope) {
  if (!(scope instanceof HTMLElement)) {
    return;
  }

  const nodes = scope.querySelectorAll('[data-video-testimonials]');
  nodes.forEach((node) => {
    const instance = sectionInstances.get(node);
    if (!instance) {
      return;
    }

    instance.destroy();
    sectionInstances.delete(node);
  });

  if (scope.matches('[data-video-testimonials]')) {
    const instance = sectionInstances.get(scope);
    if (instance) {
      instance.destroy();
      sectionInstances.delete(scope);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initVideoTestimonials(document);
  });
} else {
  initVideoTestimonials(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initVideoTestimonials(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyVideoTestimonials(event.target);
});
